import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { bookingsApi, usersApi, ApiRequestError, type Booking, FRIENDS_USER_ID, FRIENDS_USER_NAME } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import type { PublicUser } from '@apartment-booking/shared';

type BookingStatus = 'all' | 'pending' | 'approved' | 'declined' | 'cancelled';

export default function BookingsPage() {
  const { user: currentUser } = useAuth();
  const isOwner = currentUser?.role === 'owner';

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<BookingStatus>('all');
  const [userFilter, setUserFilter] = useState<string>('all');

  // Reassignment state
  const [reassignModal, setReassignModal] = useState<Booking | null>(null);
  const [reassignUserId, setReassignUserId] = useState('');
  const [reassignGuestName, setReassignGuestName] = useState('');
  const [isReassigning, setIsReassigning] = useState(false);
  const [reassignError, setReassignError] = useState<string | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [bookingsData, usersData] = await Promise.all([
        bookingsApi.getAll({
          status: statusFilter === 'all' ? undefined : statusFilter,
          user_id: userFilter === 'all' ? undefined : userFilter,
        }),
        usersApi.list(),
      ]);
      setBookings(bookingsData.bookings);
      setUsers(usersData.users);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to load bookings');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter, userFilter]);

  const handleReassign = async () => {
    if (!reassignModal || !reassignUserId) return;

    // Validate guest name for Friends
    if (reassignUserId === FRIENDS_USER_ID && !reassignGuestName.trim()) {
      setReassignError('Guest name is required for Friends bookings');
      return;
    }

    setIsReassigning(true);
    setReassignError(null);

    try {
      await bookingsApi.reassign(
        reassignModal.id,
        reassignUserId,
        reassignUserId === FRIENDS_USER_ID ? reassignGuestName.trim() : undefined
      );
      setReassignModal(null);
      setReassignUserId('');
      setReassignGuestName('');
      await fetchData();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setReassignError(err.message);
      } else {
        setReassignError('Failed to reassign booking');
      }
    } finally {
      setIsReassigning(false);
    }
  };

  const openReassignModal = (booking: Booking) => {
    setReassignModal(booking);
    setReassignUserId(booking.user_id);
    setReassignGuestName(booking.guest_name || '');
    setReassignError(null);
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = format(parseISO(startDate), 'MMM d, yyyy');
    const end = format(parseISO(endDate), 'MMM d, yyyy');
    if (start === end) return start;
    return `${start} - ${end}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDisplayName = (booking: Booking) => {
    if (booking.user_id === FRIENDS_USER_ID && booking.guest_name) {
      return `${FRIENDS_USER_NAME}: ${booking.guest_name}`;
    }
    return booking.user_name || 'Unknown';
  };

  // Filter users to get assignable users (active users only)
  const assignableUsers = users.filter(u => u.is_active);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Manage Bookings</h1>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-4">
          <div>
            <label htmlFor="status-filter" className="label">
              Status
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as BookingStatus)}
              className="input"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label htmlFor="user-filter" className="label">
              User
            </label>
            <select
              id="user-filter"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="input"
            >
              <option value="all">All Users</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Bookings table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading bookings...</p>
          </div>
        ) : error ? (
          <div className="p-6 text-center py-12">
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchData}
              className="mt-4 text-primary-600 hover:text-primary-700"
            >
              Try again
            </button>
          </div>
        ) : bookings.length === 0 ? (
          <div className="p-6 text-center py-12 text-gray-500">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No Bookings Found
            </h3>
            <p className="mt-2 text-sm">
              {statusFilter !== 'all' || userFilter !== 'all'
                ? 'Try adjusting your filters.'
                : 'No bookings have been created yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Guest
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  {isOwner && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bookings.map((booking) => (
                  <tr key={booking.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm"
                          style={{ backgroundColor: booking.user_color || '#9CA3AF' }}
                        >
                          {(booking.user_id === FRIENDS_USER_ID
                            ? booking.guest_name?.charAt(0) || 'F'
                            : booking.user_name?.charAt(0) || '?'
                          ).toUpperCase()}
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {getDisplayName(booking)}
                          </div>
                          {booking.user_id === FRIENDS_USER_ID && (
                            <div className="text-xs text-gray-500">
                              (Friends booking)
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDateRange(booking.start_date, booking.end_date)}
                      </div>
                      {booking.start_time && booking.end_time && (
                        <div className="text-xs text-gray-500">
                          {booking.start_time} - {booking.end_time}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusBadge(
                          booking.status
                        )}`}
                      >
                        {booking.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(parseISO(booking.created_at), 'MMM d, yyyy')}
                    </td>
                    {isOwner && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => openReassignModal(booking)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Reassign
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reassign Modal */}
      {reassignModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setReassignModal(null)}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Reassign Booking
              </h2>

              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600">
                  <strong>Current:</strong> {getDisplayName(reassignModal)}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Dates:</strong>{' '}
                  {formatDateRange(reassignModal.start_date, reassignModal.end_date)}
                </p>
              </div>

              {reassignError && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {reassignError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="reassign-user" className="label">
                    Assign to
                  </label>
                  <select
                    id="reassign-user"
                    value={reassignUserId}
                    onChange={(e) => {
                      setReassignUserId(e.target.value);
                      // Clear guest name if not Friends
                      if (e.target.value !== FRIENDS_USER_ID) {
                        setReassignGuestName('');
                      }
                    }}
                    className="input"
                  >
                    <option value="">Select user...</option>
                    {assignableUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                        {user.id === FRIENDS_USER_ID ? ' (for non-members)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {reassignUserId === FRIENDS_USER_ID && (
                  <div>
                    <label htmlFor="guest-name" className="label">
                      Guest Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="guest-name"
                      type="text"
                      value={reassignGuestName}
                      onChange={(e) => setReassignGuestName(e.target.value)}
                      className="input"
                      placeholder="Enter the guest's name"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This name will be displayed on the calendar.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setReassignModal(null)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReassign}
                  disabled={isReassigning || !reassignUserId}
                  className="btn-primary flex-1"
                >
                  {isReassigning ? 'Reassigning...' : 'Reassign Booking'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
